import type { CandleDto, PositionDto } from '@et/shared';
import {
  ColorType,
  LineStyle,
  createChart,
  type CandlestickData,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type UTCTimestamp,
} from 'lightweight-charts';
import { useEffect, useRef, useState } from 'react';
import { api, describeError } from '../api/client';
import { useT } from '../lib/i18n';
import { tradingViewSymbol } from '../lib/position-math';

/**
 * Candles for one open position with its levels drawn on — entry, break-even, stop and
 * take-profit — and a marker on the entry bar. Rendered with TradingView's open-source
 * Lightweight Charts; the full TradingView chart is one link away.
 */

const TIMEFRAMES = ['5m', '15m', '1h'] as const;
type Timeframe = (typeof TIMEFRAMES)[number];
const TF_SEC: Record<Timeframe, number> = { '5m': 300, '15m': 900, '1h': 3600 };
const REFRESH_MS = 30_000;

/** Lightweight Charts draws UTC; shifting by the local offset shows the viewer's clock. */
const TZ_SHIFT = -new Date().getTimezoneOffset() * 60;

function css(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** Enough decimals for cheap coins (0.03 SAGA) without cluttering BTC. */
function precisionFor(price: number): number {
  if (price >= 100) return 2;
  if (price >= 1) return 4;
  if (price >= 0.01) return 6;
  return 8;
}

function toBar(c: CandleDto): CandlestickData<UTCTimestamp> {
  return {
    time: (c.time + TZ_SHIFT) as UTCTimestamp,
    open: Number(c.open),
    high: Number(c.high),
    low: Number(c.low),
    close: Number(c.close),
  };
}

export function PositionChart({ position }: { position: PositionDto }): JSX.Element {
  const tr = useT();
  const box = useRef<HTMLDivElement | null>(null);
  const chart = useRef<IChartApi | null>(null);
  const series = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const lastBar = useRef<CandlestickData<UTCTimestamp> | null>(null);
  const lines = useRef<IPriceLine[]>([]);
  const [tf, setTf] = useState<Timeframe>('5m');
  const [error, setError] = useState<string | null>(null);

  // Chart instance: once per mount.
  useEffect(() => {
    if (!box.current) return;
    const c = createChart(box.current, {
      autoSize: true,
      layout: { background: { type: ColorType.Solid, color: css('--surface-1') }, textColor: css('--text-muted'), fontSize: 11 },
      grid: { vertLines: { color: css('--grid') }, horzLines: { color: css('--grid') } },
      rightPriceScale: { borderColor: css('--border') },
      timeScale: { borderColor: css('--border'), timeVisible: true, secondsVisible: false },
    });
    const precision = precisionFor(Number(position.avgEntry));
    series.current = c.addCandlestickSeries({
      upColor: css('--div-pos'),
      downColor: css('--div-neg'),
      wickUpColor: css('--div-pos'),
      wickDownColor: css('--div-neg'),
      borderVisible: false,
      priceFormat: { type: 'price', precision, minMove: 10 ** -precision },
    });
    chart.current = c;
    return () => {
      c.remove();
      chart.current = null;
      series.current = null;
      lines.current = [];
    };
  }, [position.avgEntry]);

  // Bars + entry marker: on timeframe change and every 30 s.
  useEffect(() => {
    let alive = true;
    const load = async (): Promise<void> => {
      try {
        const bars = await api.get<CandleDto[]>(`/market/candles?symbol=${encodeURIComponent(position.symbol)}&timeframe=${tf}&limit=300`);
        if (!alive || !series.current) return;
        const data = bars.map(toBar);
        series.current.setData(data);
        lastBar.current = data.at(-1) ?? null;
        const opened = Math.floor(Date.parse(position.openedAt) / 1000 / TF_SEC[tf]) * TF_SEC[tf] + TZ_SHIFT;
        series.current.setMarkers([{ time: opened as UTCTimestamp, position: 'belowBar', color: css('--series-1'), shape: 'arrowUp', text: tr('BUY') }]);
        setError(null);
      } catch (err) {
        if (alive) setError(describeError(err));
      }
    };
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [position.symbol, position.openedAt, tf, tr]);

  // Live tick: stretch the newest bar to the latest price between refreshes.
  useEffect(() => {
    const bar = lastBar.current;
    if (!bar || !series.current || position.lastPrice === null) return;
    const price = Number(position.lastPrice);
    const next = { ...bar, close: price, high: Math.max(bar.high, price), low: Math.min(bar.low, price) };
    lastBar.current = next;
    series.current.update(next);
  }, [position.lastPrice]);

  // Levels: redrawn whenever the stop ratchets or the targets change.
  useEffect(() => {
    const s = series.current;
    if (!s) return;
    for (const l of lines.current) s.removePriceLine(l);
    const levels: { price: string | null; color: string; title: string; style: LineStyle }[] = [
      { price: position.avgEntry, color: css('--text-secondary'), title: tr('Entry'), style: LineStyle.Solid },
      { price: position.breakEven, color: css('--text-muted'), title: tr('Break-even'), style: LineStyle.Dotted },
      { price: position.currentStop, color: css('--div-neg'), title: tr('Stop'), style: LineStyle.Dashed },
      { price: position.currentTp, color: css('--div-pos'), title: tr('Target'), style: LineStyle.Dashed },
    ];
    lines.current = levels
      .filter((l): l is typeof l & { price: string } => l.price !== null)
      .map((l) => s.createPriceLine({ price: Number(l.price), color: l.color, lineWidth: 1, lineStyle: l.style, axisLabelVisible: true, title: l.title }));
  }, [position.avgEntry, position.breakEven, position.currentStop, position.currentTp, tr]);

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div className="row" role="group" aria-label={tr('Timeframe')}>
          {TIMEFRAMES.map((x) => (
            <button key={x} className={`small ${x === tf ? 'primary' : ''}`} onClick={() => { setTf(x); }}>{x}</button>
          ))}
        </div>
        <a href={`https://www.tradingview.com/chart/?symbol=${tradingViewSymbol(position.symbol)}`} target="_blank" rel="noreferrer">
          {tr('Open in TradingView ↗')}
        </a>
      </div>
      {error && <div className="neg">{error}</div>}
      <div ref={box} className="position-chart" />
    </div>
  );
}
